<script setup lang="ts" generic="T extends SelectInputType, ValueKey extends string, LabelKey extends string">
import {
  computed,
  toRef,
} from 'vue';

import type {
  IFormSelectInputOptionWithExtendedProps,
  SelectInputType,
} from '@modules/forms/types';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VFormFloatLabelInputWrapper
  from '@modules/forms/components/inputs/VFormFloatLabelInputWrapper/VFormFloatLabelInputWrapper.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { TRANSLATION_GROUP_DROPDOWN } from '@modules/forms/constants';
import { isIOSDevice } from '@utils/browser';
import { watchImmediate } from '@vueuse/core';

import type {
  VFormSelectInputEmits,
  VFormSelectInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormSelectInputProps<T, ValueKey, LabelKey>>(), {});
const emit = defineEmits<VFormSelectInputEmits<T>>();

const isIOS = isIOSDevice();

const { innerValue } = useInputInnerValue<T>(toRef(props, 'value'));

const isBigData = computed(() => props.options.length > 50);
const isFilterable = computed(() => props.options.length > 6);
const options = toRef(props, 'options');

const valueType = computed<ValueKey>(() => props.optionValue || 'value' as ValueKey);
const labelType = computed<LabelKey>(() => props.optionLabel || 'label' as LabelKey);

watchImmediate(options, (newValue, oldValue) => {
  if (
    innerValue.value == null && newValue.length > 0 && (!oldValue || oldValue.length === 0)
    && (props.autoSelectFirst || newValue.length === 1)
  ) {
    innerValue.value = newValue[0][valueType.value];
    emit('change', innerValue.value);
  }
});

const selectedValue = computed<IFormSelectInputOptionWithExtendedProps<T, ValueKey, LabelKey> | undefined>(() => {
  return props.options.find((item) => item[valueType.value] === innerValue.value);
});

const selectedValueLabel = computed<string | undefined>(() => {
  if (selectedValue.value) {
    return selectedValue.value[labelType.value] as string;
  }
  return undefined;
});

const readonlySelectedValueLabel = computed<string | undefined>(() => {
  if (!props.readonly) {
    return '';
  }
  return selectedValueLabel.value;
});

const selectedValueImage = computed<string | undefined>(() => {
  if (selectedValue.value?.image) {
    return selectedValue.value.image as string;
  }
  return undefined;
});
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormSelectInput'"
    :hide-errors="hideErrors"
    :readonly="readonly"
    :errors="errors"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    :description="description"
    :description-severity="descriptionSeverity"
  >
    <VFormFloatLabelInputWrapper
      :name="name"
      :label="label"
      :icon-left="iconLeft"
      :icon-right="iconRight"
      :required="required"
      :readonly="readonly"
      :image="selectedValueImage"
    >
      <PInputText
        v-if="readonly"
        v-model="readonlySelectedValueLabel"
        :name="name"
        :readonly="readonly"
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly, 'input-readonly': readonly }"
      />
      <PInputDropdown
        v-else
        v-model="innerValue"
        :name="name"
        :input-id="name"
        :placeholder="placeholder"
        :virtual-scroller-options="isBigData ? {
          itemSize: 38,
        } : undefined"
        :required="required"
        :disabled="disabled"
        :options="options"
        :loading="loading"
        :option-label="labelType"
        :option-value="valueType"
        :option-disabled="optionDisabled"
        :filter="isFilterable"
        :auto-filter-focus="!isIOS"
        :filter-placeholder="$getTranslation(TRANSLATION_GROUP_DROPDOWN, 'PLACEHOLDER')"
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly, 'input-readonly': readonly }"
        :pt="{
          root: {
            autocomplete: autocomplete ? autocomplete : 'off',
          },
        }"
        @hide="emit('blur', innerValue)"
        @show="emit('focus', innerValue)"
        @change="emit('change', innerValue)"
        @input="emit('input', innerValue)"
      >
        <template #header>
          <VMessage
            v-if="headerMessage"
            class="input-max-width"
            :severity="headerMessageSeverity || 'info'"
          >
            {{ headerMessage }}
          </VMessage>
        </template>
        <template #value="scope">
          <div
            v-if="selectedValueLabel"
            class="flex items-center gap-2"
          >
            <img
              v-if="selectedValueImage"
              :src="selectedValueImage"
              class="max-h-5 max-w-5"
            >
            <div>{{ selectedValueLabel }}</div>
          </div>
          <span v-else>
            {{ scope.placeholder || '&nbsp;' }}
          </span>
        </template>
        <template #option="{ option }">
          <div class="flex items-center gap-2">
            <img
              v-if="option.image"
              :src="option.image"
              class="max-h-7 max-w-7"
            >
            <div>{{ option[labelType] }}</div>
          </div>
        </template>
        <template #filtericon>
          <i class="pi pi-search" />
        </template>
      </PInputDropdown>
    </VFormFloatLabelInputWrapper>
  </VFormInputWrapper>
</template>
